import React from "react";
import { AlertCircle, CheckCircle2, ChevronDown, CircleDashed, FileText, Info, Link2 } from "lucide-react";

// Shared look for the tender screens, kept in one place so the results list and
// the tender page cannot drift apart on what a pill or a deadline looks like.
// Colours and type sizes are the app's own: #C95EBE / #BB42AE brand, #8B55DD
// purple, the #18101C–#9CA3AF ink ramp, Outfit at the 11/12/13/14/15px scale
// the rest of the screens use.

const PURPLE = "#8B55DD";

// ── One frame for Search and Discover ───────────────────────────────────────
// The two modes are separate pages, so these hold them to the same rail width,
// top bar and content width: switching between them moves nothing on screen.
const RAIL_BASE = "w-[272px] shrink-0 bg-white border-r border-black/10 overflow-y-auto custom-scrollbar";
export const RAIL_CLASS = `hidden lg:block ${RAIL_BASE}`;
// With a tender open beside the list, the rail gives way until the screen is
// wide enough for rail, list and tender all at once.
export const RAIL_CLASS_WITH_PANEL = `hidden 2xl:block ${RAIL_BASE}`;
export const TOP_BAR_CLASS = "flex-shrink-0 bg-white px-6 pt-4 pb-4 border-b border-black/10";
export const CONTENT_CLASS = "max-w-[1180px]";
// Section titles inside either rail.
export const RAIL_HEADING = "font-outfit text-[12px] font-semibold uppercase tracking-[0.06em] text-[#52525B]";
// The tinted block at the top of either rail (Saved searches / Your match profile).
export const RAIL_HEAD_BLOCK = "bg-[#FCF8FC] border-b border-[#F1F1F3] px-[18px] pt-3.5 pb-3";

// A line of work is proven when a delivered project in Memory shows it; the
// badge names those projects on hover, so "proven" can be checked. One badge
// for the Discover rail and the setup screen alike.
export function ProvenBadge({ proof = [] }) {
  return proof.length ? (
    <span
      title={`Proven by: ${proof.join("; ")}`}
      className="inline-flex flex-none items-center gap-1 rounded-full border border-[#86EFAC] bg-[#F0FDF4] px-2 py-[2px] font-outfit text-[11.5px] font-semibold text-[#15803D]"
    >
      <CheckCircle2 className="h-3 w-3" strokeWidth={2.4} />
      Proven
    </span>
  ) : (
    <span
      title="No delivered project in your Memory shows this work yet"
      className="inline-flex flex-none items-center gap-1 rounded-full border border-[#E4E4E7] bg-[#F4F4F5] px-2 py-[2px] font-outfit text-[11.5px] font-semibold text-[#71717A]"
    >
      <CircleDashed className="h-3 w-3" strokeWidth={2.4} />
      Not yet proven
    </span>
  );
}

// Sort, in the same place and the same size in both modes. appearance-none: the
// platform's own arrow and padding made it sit differently from everything
// around it; still a real select underneath, so it keeps its keyboard behaviour.
export function SortSelect({ id, value, onChange, options }) {
  return (
    <div className="ml-auto flex items-center gap-2">
      <label htmlFor={id} className="font-outfit text-[13px] text-[#00000080] font-medium">
        Sort
      </label>
      <div className="relative">
        <select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none h-[35px] font-outfit text-[14px] border border-[#00000036]
                     rounded-md pl-2.5 pr-8 bg-white text-[#52525B] outline-none
                     cursor-pointer focus:border-[#C95EBE]"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown
          className="w-3.5 h-3.5 text-[#52525B] absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
          strokeWidth={2}
        />
      </div>
    </div>
  );
}

const NOTICE_TONE = {
  combined_synopsis: "bg-[#F5F3FF] border-[#C4B5FD] text-[#7C3AED]",
  solicitation: "bg-[#F0FDF4] border-[#86EFAC] text-[#16A34A]",
  presolicitation: "bg-[#FFF7ED] border-[#FDBA74] text-[#EA580C]",
  sources_sought: "bg-[#FEFCE8] border-[#FDE68A] text-[#A16207]",
  special_notice: "bg-[#EFF6FF] border-[#93C5FD] text-[#2563EB]",
  award: "bg-[#F4F4F5] border-[#E4E4E7] text-[#52525B]",
  other: "bg-[#F4F4F5] border-[#E4E4E7] text-[#71717A]",
};

export const URGENCY_TEXT = {
  passed: "text-[#9CA3AF]",
  urgent: "text-[#DC2626]",
  soon: "text-[#EA580C]",
  ok: "text-[#52525B]",
  none: "text-[#9CA3AF]",
};

const PILL_BASE =
  "inline-block font-outfit text-[11px] font-semibold rounded-full px-2.5 py-[3px] border whitespace-nowrap";

export function NoticePill({ type, label }) {
  if (!label) return null;
  return <span className={`${PILL_BASE} ${NOTICE_TONE[type] || NOTICE_TONE.other}`}>{label}</span>;
}

export function SetAsidePill({ label }) {
  if (!label) return null;
  return <span className={`${PILL_BASE} bg-[#EFF6FF] border-[#93C5FD] text-[#2563EB]`}>{label}</span>;
}

export function ImportedPill({ children = "Already in a project" }) {
  return <span className={`${PILL_BASE} bg-[#F0FDF4] border-[#86EFAC] text-[#16A34A]`}>{children}</span>;
}

// Which portal a tender came from. Federal only today, so the badge matters
// more once state and local sources land beside it.
export function SourceBadge({ label }) {
  if (!label) return null;
  return (
    <span
      className="inline-flex items-center gap-1.5 font-outfit text-[11px] font-semibold text-[#71717A]
                 bg-white border border-[#E5E7EB] rounded-full pl-2 pr-2.5 py-[3px] whitespace-nowrap"
    >
      <i className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: PURPLE }} />
      {label}
    </span>
  );
}

// Attachments have three states, not two. The nightly bulk feed carries no
// attachment column, so "no documents" would be a lie about ~79,000 tenders.
//
// Links are counted apart from files on purpose. A third of tenders attach
// nothing but web links, and there is nothing behind one to download: rolling
// them into a document count is what put "4 documents" on a row that produces
// an empty project.
export function DocumentCount({ hasDocuments, count = 0, linkCount = 0 }) {
  if (count > 0) {
    return (
      <span className="inline-flex items-center gap-1.5 font-outfit text-[12px] text-[#71717A]">
        <FileText className="w-[13px] h-[13px] text-[#9CA3AF]" />
        {count} document{count === 1 ? "" : "s"}
        {linkCount > 0 && (
          <span className="text-[#9CA3AF]">
            + {linkCount} link{linkCount === 1 ? "" : "s"}
          </span>
        )}
      </span>
    );
  }

  if (linkCount > 0) {
    return (
      <span
        className="inline-flex items-center gap-1.5 font-outfit text-[12px] text-[#9CA3AF]"
        title="The agency published web links rather than files. There is nothing to download into a project."
      >
        <Link2 className="w-[13px] h-[13px]" />
        {linkCount} link{linkCount === 1 ? "" : "s"}, no files
      </span>
    );
  }

  if (hasDocuments === null) {
    return (
      <span
        className="inline-flex items-center gap-1.5 font-outfit text-[12px] text-[#9CA3AF]"
        title="Our nightly feed does not list attachments. Open the tender and we check the source for you."
      >
        <FileText className="w-[13px] h-[13px]" />
        Documents not checked yet
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 font-outfit text-[12px] text-[#9CA3AF]">
      <FileText className="w-[13px] h-[13px]" />
      No documents
    </span>
  );
}

const CALLOUT_TONE = {
  amber: "bg-[#FEFCE8] border-[#FDE68A] text-[#854D0E]",
  neutral: "bg-white border-[#E5E7EB] text-[#52525B]",
  danger: "bg-[#FEF2F2] border-[#FCA5A5] text-[#B91C1C]",
};

// Every note that says what a filter is hiding uses this, so they read as one
// voice rather than three different warnings.
export function Callout({ tone = "neutral", children, action, onAction }) {
  const Icon = tone === "danger" ? AlertCircle : Info;
  return (
    <div
      className={`flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5 mb-3 font-outfit text-[13px] leading-[1.5] ${CALLOUT_TONE[tone]}`}
    >
      <Icon className="w-[15px] h-[15px] shrink-0 mt-[1px]" />
      <div className="flex-1">
        {children}
        {action && (
          <button
            type="button"
            onClick={onAction}
            className="ml-1.5 font-outfit font-semibold underline underline-offset-2 hover:opacity-80"
          >
            {action}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tender descriptions. Shared by the tender page and Sensa's tender panel, so
// both render an agency's description the same way.
// ---------------------------------------------------------------------------

// Agencies draw section dividers by hand, out of whatever punctuation is to
// hand. Measured over the corpus: 20 or more of one character is always a
// divider, while shorter runs are emphasis ("***See note") or a form blank
// ("returning ______ copies"), so the cut has to sit between them.
export const RULE_RUN = /([*=_~#-])\1{19,}/g;

// The flattened text carries no markup to hang a rule on, so it is split.
export function DescriptionText({ text }) {
  // split() hands back the captured character too, so only every other
  // item is real text.
  const parts = text.split(RULE_RUN).filter((_, i) => i % 2 === 0);
  return parts.map((part, i) => (
    <React.Fragment key={i}>
      {i > 0 && <hr />}
      {part.trim() ? <p className="whitespace-pre-line">{part.trim()}</p> : null}
    </React.Fragment>
  ));
}

// Close the paragraph a divider sat in and open a fresh one after it, or the
// text that followed it ends up loose and unstyled. Only inert punctuation is
// replaced, so nothing reaches the markup that the server-side sanitiser did
// not already pass.
export const withRules = (html) =>
  html
    .replace(RULE_RUN, "</p><hr /><p>")
    .replace(/<p>\s*<\/p>\s*<hr \/>/g, "<hr />")
    .replace(/<hr \/>\s*<p>\s*<\/p>/g, "<hr />");

// Agency descriptions are pasted out of Word, so they arrive with whatever
// structure the buyer used. These give headings, lists and tables a shape that
// belongs to our type scale rather than to Word's.
// break-words: agencies type rules out of asterisks and blanks out of
// underscores, and a 100-character run has nowhere to wrap without it.
export const DESCRIPTION_PROSE = [
  "font-outfit text-[14px] text-[#52525B] leading-[1.75] break-words",
  "[&_p]:mb-3 [&_p:last-child]:mb-0",
  "[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ul]:space-y-1",
  "[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_ol]:space-y-1",
  "[&_li]:leading-[1.6] [&_li]:pl-0.5",
  "[&_strong]:font-semibold [&_strong]:text-[#3F3F46]",
  "[&_b]:font-semibold [&_b]:text-[#3F3F46]",
  "[&_h1]:text-[16px] [&_h2]:text-[15px] [&_h3]:text-[14px]",
  "[&_h4]:text-[14px] [&_h5]:text-[13px] [&_h6]:text-[13px]",
  "[&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold",
  "[&_h4]:font-semibold [&_h5]:font-semibold [&_h6]:font-semibold",
  "[&_h1]:text-[#18101C] [&_h2]:text-[#18101C] [&_h3]:text-[#18101C]",
  "[&_h4]:text-[#18101C] [&_h5]:text-[#3F3F46] [&_h6]:text-[#3F3F46]",
  "[&_h1]:mt-5 [&_h2]:mt-5 [&_h3]:mt-4 [&_h4]:mt-4",
  "[&_h1]:mb-2 [&_h2]:mb-2 [&_h3]:mb-1.5 [&_h4]:mb-1.5",
  "[&_h1:first-child]:mt-0 [&_h2:first-child]:mt-0 [&_h3:first-child]:mt-0",
  "[&_a]:text-[#BB42AE] [&_a]:underline [&_a]:underline-offset-2 [&_a]:break-words",
  "[&_table]:w-full [&_table]:my-3 [&_table]:text-[13px] [&_table]:border-collapse",
  "[&_th]:border [&_th]:border-[#E5E7EB] [&_th]:bg-[#FAFAFA] [&_th]:px-2.5 [&_th]:py-1.5 [&_th]:text-left [&_th]:font-semibold [&_th]:text-[#3F3F46]",
  "[&_td]:border [&_td]:border-[#E5E7EB] [&_td]:px-2.5 [&_td]:py-1.5 [&_td]:align-top",
  "[&_hr]:my-4 [&_hr]:border-[#E5E7EB]",
  "[&_blockquote]:border-l-2 [&_blockquote]:border-[#E5E7EB] [&_blockquote]:pl-3 [&_blockquote]:text-[#71717A]",
  "[&_pre]:whitespace-pre-wrap [&_pre]:break-words [&_pre]:bg-[#FAFAFA] [&_pre]:rounded-lg [&_pre]:p-3 [&_pre]:text-[13px]",
  "[&_code]:bg-[#F4F4F5] [&_code]:rounded [&_code]:px-1 [&_code]:py-[1px] [&_code]:text-[13px]",
].join(" ");
